import { ApolloError } from '@apollo/client';
import type * as Sentry from '@sentry/react';
import { excludeGraphQLFetch } from 'apollo-link-sentry';

import { UnauthorizedError, isApolloSessionExpiry, isSessionExpiryCode } from '@/core/api';
import {
  redactInviteInBreadcrumb,
  redactInviteInEvent,
  redactInviteInReplayEvent,
  redactInviteInReplayRecordingEvent,
} from '@/core/sentryRedact';

export type TransactionEvent = Parameters<
  NonNullable<Sentry.BrowserOptions['beforeSendTransaction']>
>[0];

export type RecordingEvent = Parameters<
  NonNullable<
    NonNullable<Parameters<typeof Sentry.replayIntegration>[0]>['beforeAddRecordingEvent']
  >
>[0];

// A redaction bug must never cost the event: on a throw, send it unredacted.
function safeRedact<E>(event: E, redact: (event: E) => E): E {
  try {
    return redact(event);
  } catch {
    return event;
  }
}

export function sentryBeforeSend(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): Sentry.ErrorEvent | null {
  // Drop only events that carry neither an exception nor a message. captureMessage
  // events reach beforeSend too — Sentry classifies every event without a `type` as an
  // error event — and with `attachStacktrace` unset they never gain an `exception`, so
  // testing `!event.exception` alone discarded all of them client-side. That silently
  // swallowed the warning ENG-5628 leans on as its only remaining signal once the
  // throw is gone, and ENG-5528's before it.
  if (!event.exception && !event.message) {
    return null;
  }
  // Mid-session JWT expiry: handleError in src/core/api.ts redirects the
  // user to /login, but the throw races the navigation and Sentry's
  // global handlers can still fire on the unhandled rejection (Sentry
  // WEBAPP-BW1) or the apollo-link-sentry capture of the underlying
  // ApolloError 401 (Sentry WEBAPP-BW2). Both originate from a session
  // boundary the user is recovering from — not an application bug.
  const orig = hint?.originalException;
  if (orig instanceof UnauthorizedError && isSessionExpiryCode(orig.code)) {
    return null;
  }
  if (orig instanceof ApolloError && isApolloSessionExpiry(orig)) {
    return null;
  }
  // The page URL (request.url) can hold an invitation JWT. ENG-6105.
  return safeRedact(event, redactInviteInEvent);
}

// Transactions get the same request.url from the HttpContext integration, and
// every pageload of an invitation link is sampled.
export function sentryBeforeSendTransaction(event: TransactionEvent): TransactionEvent {
  return safeRedact(event, redactInviteInEvent);
}

// Replay records DOM attributes in snapshots and mutations, out of reach of the
// hooks below, and auth links put the invitation JWT in their href. Blocked
// elements keep only their class and size. maskAttributes cannot cover href:
// rrweb's transformAttribute returns absoluteToDoc for href before it calls
// maskAttributeFn (@sentry-internal/replay 8.55.1). `invite%25` catches every
// further encoding pass (a nested next gives invite%253D). ENG-6160.
export const sentryReplayBlock = [
  'a[href*="invite=" i], a[href*="invite%3D" i], a[href*="invite%25" i]',
];

// Session Replay segments never reach beforeSend. Navigation and request spans,
// and the click breadcrumbs replay records itself, pass through
// beforeAddRecordingEvent (custom events only; the SDK drops an event whose
// callback throws, hence safeRedact). ENG-6160.
export function sentryBeforeAddRecordingEvent(event: RecordingEvent): RecordingEvent {
  return safeRedact(event, redactInviteInReplayRecordingEvent);
}

// The replay event's `urls` list (window.location at segment start, then every
// history push) and the request.url HttpContext adds to it are only reachable
// from an event processor: replay sends it through prepareEvent, not beforeSend.
export function sentryProcessReplayEvent(event: Sentry.Event): Sentry.Event {
  if (event.type !== 'replay_event') return event;
  return safeRedact(event, redactInviteInReplayEvent);
}

export function sentryBeforeBreadcrumb(
  breadcrumb: Sentry.Breadcrumb,
  hint?: Sentry.BreadcrumbHint
): Sentry.Breadcrumb | null {
  const kept = excludeGraphQLFetch(breadcrumb, hint);
  if (!kept) return kept;
  try {
    return redactInviteInBreadcrumb(kept);
  } catch {
    return kept;
  }
}
